/**
 * Building — and reading back — the `?to=` a session bounce leaves on `/login`.
 *
 * Three call sites construct that URL (the route guard and the two token-refresh
 * rejection paths in `urqlClient`), and one reads it (the login form). They used
 * to do it inline, which is how both of the defects below shipped: the shape of
 * the URL was nobody's job in particular.
 */

const LOGIN_PATH = '/login'

/**
 * The path to come back to after signing in, or null when there is none worth
 * keeping.
 *
 * TWO THINGS THIS REFUSES, both of which were live:
 *
 *  - **`/login` itself.** A rejection fired while already sitting on the login
 *    page captured the login URL as the return target, producing
 *    `?to=%2Flogin%3Freason%3Drevoked%26to%3D%252Fprojects…` — the real
 *    destination buried one nesting level deeper on every pass, and a sign-in
 *    that returned the reader to the sign-in page. When the current page is
 *    already `/login`, its own `to` is carried through instead, so the original
 *    destination survives any number of bounces.
 *
 *  - **Anything that is not a same-origin path.** `to` arrives from the address
 *    bar, so `?to=https://evil.example` or `?to=//evil.example` must never be
 *    followed after a successful login — that is an open redirect, and a
 *    credible one precisely because it is handed to someone who has just typed
 *    their password. Only a path starting with a single `/` is accepted.
 */
export const safeReturnTo = (raw: string | null | undefined): string | null => {
  if (!raw) return null
  // `//host` and `/\host` are protocol-relative: browsers treat them as absolute.
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) {
    return null
  }
  const [path, query = ''] = splitPath(raw)
  if (path === LOGIN_PATH) {
    // Already a login URL: keep what IT was trying to return to, not itself.
    return safeReturnTo(new URLSearchParams(query).get('to'))
  }
  return raw
}

const splitPath = (url: string): [string, string?] => {
  const i = url.indexOf('?')
  return i === -1 ? [url] : [url.slice(0, i), url.slice(i + 1)]
}

/**
 * The `/login` URL to bounce to, carrying why and where back.
 *
 * `currentPath` is the full path+search of the page being left.
 */
export const buildLoginRedirect = (
  reason: string,
  currentPath: string,
): string => {
  const params = new URLSearchParams(reason.includes('=') ? reason : { reason })
  const target = safeReturnTo(currentPath)
  if (target && target !== '/') params.set('to', target)
  else params.delete('to')
  return `${LOGIN_PATH}?${params.toString()}`
}
